import { type AnswerRevealMode } from '@/shared/utils/answerRevealMode';
import { AiTutorBadge } from './AiTutorBadge';
import { AllowPassBadge } from './AllowPassBadge';
import { PublicBadge } from './PublicBadge';
import { ShowAnswerBadge } from './ShowAnswerBadge';
import { TranslationLanguagesBadge } from './TranslationLanguagesBadge';
export interface BadgeListProps {
  /** 통과 허용 여부 */
  isAllowPass?: boolean;
  /** 정답 표시 여부 */
  showIncorrectAnswer: boolean;
  /** 정답 공개 방식 */
  answerRevealMode?: AnswerRevealMode;
  /** 공개 여부 */
  isPublic: boolean;
  /** AI 튜터 활성화 여부 */
  aiTutorEnabled: boolean;
  /** AI 번역 활성화 여부 */
  isAiTranslationEnabled?: boolean;
  /** 번역 언어 목록 */
  aiTranslationLanguages?: string[];
}

/**
 * Divider - 배지 사이 구분선
 */
const Divider = () => {
  return <div className="h-[12px] w-[1px] shrink-0 bg-gray-300" data-sentry-component="Divider" data-sentry-source-file="BadgeList.tsx" />;
};

/**
 * BadgeList - 퀴즈 옵션 배지 목록 컨테이너
 *
 * @description
 * 퀴즈의 다양한 옵션들을 배지 형태로 표시합니다.
 * 배지 사이에는 Divider를 삽입하여 구분합니다.
 *
 * 표시되는 배지:
 * - 통과 허용
 * - 정답 공개 방식
 * - 공개/비공개
 * - AI 튜터 (조건부: aiTutorEnabled가 true일 때만)
 */
export const BadgeList = ({
  isAllowPass = true,
  answerRevealMode,
  showIncorrectAnswer,
  isPublic,
  aiTutorEnabled,
  isAiTranslationEnabled,
  aiTranslationLanguages
}: BadgeListProps) => {
  const showTranslation = isAiTranslationEnabled && aiTranslationLanguages && aiTranslationLanguages.length > 0;
  return <div className="flex min-h-px w-full min-w-px flex-wrap items-center gap-xs tablet-lg:w-auto tablet-lg:flex-1" data-sentry-component="BadgeList" data-sentry-source-file="BadgeList.tsx">
      {/* 통과 허용 배지 */}

      <div className="flex h-[30px] shrink-0 items-center justify-center gap-sm">
        <AllowPassBadge isAllowPass={isAllowPass} data-sentry-element="AllowPassBadge" data-sentry-source-file="BadgeList.tsx" />
        <Divider data-sentry-element="Divider" data-sentry-source-file="BadgeList.tsx" />
      </div>

      {/* 정답 공개 방식 배지 */}
      <div className="flex h-[30px] shrink-0 items-center justify-center gap-sm">
        <ShowAnswerBadge answerRevealMode={answerRevealMode} showIncorrectAnswer={showIncorrectAnswer} data-sentry-element="ShowAnswerBadge" data-sentry-source-file="BadgeList.tsx" />
        <Divider data-sentry-element="Divider" data-sentry-source-file="BadgeList.tsx" />
      </div>

      {/* 공개/비공개 배지 */}
      <div className="flex h-[30px] shrink-0 items-center justify-center gap-sm">
        <PublicBadge isPublic={isPublic} data-sentry-element="PublicBadge" data-sentry-source-file="BadgeList.tsx" />
        {(showTranslation || aiTutorEnabled) && <Divider />}
      </div>

      {/* 번역 언어 배지 - 조건부 렌더링 */}
      {showTranslation && <div className="flex h-[30px] shrink-0 items-center justify-center gap-sm">
          <TranslationLanguagesBadge languages={aiTranslationLanguages} />
          {aiTutorEnabled && <Divider />}
        </div>}

      {/* 학습 도우미 배지 - 조건부 렌더링 */}
      {aiTutorEnabled && <div className="flex h-[30px] shrink-0 items-center justify-center gap-0">
          <AiTutorBadge />
        </div>}
    </div>;
};